import React, { Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { StaticQuery, graphql, withPrefix } from 'gatsby';
import { injectIntl } from 'react-intl';
import { getTranslator } from '@arcblock/www/src/libs/locale';

import Helmet from 'react-helmet';
import AsyncComponent from '@arcblock/ux/lib/Async';
import LoginButton from '@arcblock/did-connect/lib/LoginButton';
import Container from '@material-ui/core/Container';

import SidebarResponsive from './sidebar/responsive';
import SidebarWrapper from './sidebar/wrapper';
import SidebarContent from './sidebar/content';
import SidebarToggle from './sidebar/toggle';

import { DesktopHeader, MobileHeader } from './header/index';
import ListNav from './header/links/list';
import DropdownNav from './header/links/dropdown';
import SEO from './header/seo';
import Logo from './header/logo';
import Language from './header/language';

import FlexWrapper from './wrapper/flex';

const Search = AsyncComponent(() => import('./header/search'));

const appDid = process.env.GATSBY_DID_CONNECT_APP_DID;
const serviceHost = process.env.GATSBY_DID_CONNECT_SERVICE;
const enableLogin = appDid && serviceHost;

function Layout(props) {
  const t = getTranslator(props.intl);
  const mainRef = useRef(null);
  const isPathActive = value => props.location.pathname.indexOf(value) >= 0;

  // <link rel="stylesheet" type="text/css" href="//www.arcblock.io/icons/css/all.css" />
  return (
    <StaticQuery
      query={graphql`
        {
          site {
            siteMetadata {
              title
              description
              sidebarWidth
              logoUrl
              siteUrl
              disableI18n
            }
          }
        }
      `}
      render={data => {
        const { site } = data;
        return (
          <Fragment>
            <Helmet defaultTitle={site.siteMetadata.title} titleTemplate={`%s - ${site.siteMetadata.title}`}>
              <meta name="description" content={site.siteMetadata.description} />
              <link rel="icon" href={withPrefix('/favicon.ico')} />
            </Helmet>
            <SEO
              title={props.title}
              description={props.description || site.siteMetadata.description}
              siteName={site.siteMetadata.title}
            />
            <SidebarResponsive>
              {({ sidebarRef, onWrapperClick, openSidebar, sidebarOpen }) => (
                <FlexWrapper onClick={onWrapperClick}>
                  <SidebarWrapper
                    responsive
                    open={sidebarOpen}
                    ref={sidebarRef}
                    meta={site.siteMetadata}
                    version={props.version}>
                    <SidebarContent
                      title={site.siteMetadata.title}
                      pathname={props.location.pathname}
                      contents={props.sidebarContent}
                      isPathActive={isPathActive}
                    />
                  </SidebarWrapper>
                  <Main ref={mainRef} tabIndex={0}>
                    <MobileHeader>
                      <Logo version={props.version} />
                      <DropdownNav navItems={props.navItems} isPathActive={isPathActive} />
                      {enableLogin && (
                        <LoginButton
                          locale={props.locale}
                          btnSize="small"
                          btnColor="primary"
                          signInText={t('menu.login')}
                          signOutText={t('menu.logout')}
                          className="login-button"
                          style={{ marginLeft: 16 }}
                        />
                      )}
                      {!site.siteMetadata.disableI18n && <Language />}
                      <SidebarToggle onClick={openSidebar} />
                    </MobileHeader>
                    <DesktopHeader>
                      <ListNav
                        navItems={props.navItems}
                        pathname={props.location.pathname}
                        isPathActive={isPathActive}
                      />
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}>
                        <Search />
                        {enableLogin && (
                          <LoginButton
                            locale={props.locale}
                            style={{ marginLeft: 16 }}
                            btnSize="small"
                            btnColor="primary"
                            signInText={t('menu.login')}
                            signOutText={t('menu.logout')}
                            className="login-button"
                          />
                        )}
                        {!site.siteMetadata.disableI18n && <Language />}
                      </div>
                    </DesktopHeader>
                    <Container style={{ maxWidth: 1440, paddingLeft: 0, paddingRight: 0 }}>
                      {props.children({ mainRef })}
                    </Container>
                  </Main>
                </FlexWrapper>
              )}
            </SidebarResponsive>
          </Fragment>
        );
      }}
    />
  );
}

Layout.propTypes = {
  locale: PropTypes.string.isRequired,
  location: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  version: PropTypes.string.isRequired,
  navItems: PropTypes.array.isRequired,
  sidebarContent: PropTypes.any.isRequired,
  children: PropTypes.node.isRequired,
  intl: PropTypes.object.isRequired,
};

const Main = styled.main`
  flex-grow: 1;
  outline: none;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

export const themeConfig = {
  typography: {
    fontSize: 14,
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ],
  },
};

export default injectIntl(Layout);
